/**
 * Section geometry — the ONE place the page-grid / section-grid row math
 * lives. The editor (`sectionInsert.ts`, `usePageNode.ts`) and the headless
 * authoring API (`doc.ts`, driven by builder-mcp) both size section blocks
 * from these, so a template authored headlessly lands at the same height the
 * editor would auto-grow it to.
 */
import type { GridConfig } from "@fredericrous/grid/types";

/** Page-node width on the sitemap canvas (mirrors `PAGE_WIDTH` in the editor). */
export const PAGE_WIDTH = 1440;
/** Seed height stamped on a fresh page node so xyflow shows it from frame 1
 *  (see `initialSitemap.ts`); the ResizeObserver overwrites it on mount. */
export const PAGE_SEED_HEIGHT = 600;

export const PAGE_ROW_HEIGHT_PX = 60;
export const PAGE_GAP_PX = 8;
export const PAGE_COLUMNS = 12;

/** Config for a section's nested grid — same shape as the page grid.
 *  Exported so PageNode can create the shell before binding on reload. */
export const SECTION_GRID_CONFIG: Omit<GridConfig, "id"> = {
  columns: PAGE_COLUMNS,
  rowHeight: PAGE_ROW_HEIGHT_PX,
  gap: PAGE_GAP_PX,
  padding: 8,
  compactType: "vertical",
  // grid 0.8: widgets inside a section are small and numerous — the LIVE
  // (WYSIWYG, reflow-as-you-move) strategy fits; the PAGE canvas above uses
  // "gated" (PAGE_GRID_CONFIG) because section blocks are big and fast
  // sweeps shouldn't reflow the whole page. Explicit so the two-mode
  // decision is visible here, not implied by a default.
  dragStrategy: "live",
  // Select→drill-in (grid 0.8.40): a press on an unselected widget only SELECTS
  // it; a selected widget drags on move, and a second click / double-click
  // drills into content edit. Kills the "meant to type, moved it" mode error.
  // The PAGE grid stays "press" — sections drag by their header handle, not the
  // body — so this only governs widgets inside a section.
  activation: "select-first",
};

/** A section's NESTED grid — same as {@link SECTION_GRID_CONFIG} but with NO
 *  padding. A section block already sits inside the page grid's padding, so an
 *  extra 8px of nested padding pushed the body content 8px right of the
 *  header/footer bands (which live at the page-node edge) — breaking WYSIWYG
 *  alignment (user report 2026-07-20). With padding 0 the section content is
 *  flush with the block, landing at the same left edge as the bands and the
 *  published page. The bands keep {@link SECTION_GRID_CONFIG} (their padding
 *  is what puts THEIR content at that same edge). */
export const SECTION_NESTED_CONFIG: Omit<GridConfig, "id"> = {
  ...SECTION_GRID_CONFIG,
  padding: 0,
};

/** Px of section chrome above the nested grid body. Since the section chrome
 *  became SELECTION-ONLY overlays (SectionWidget: no always-on header band —
 *  the resting section matches the published page for WYSIWYG), the nested
 *  grid is flush to the block and there is no layout header to budget for.
 *  Kept as a named constant (0) so the fit math stays legible and a future
 *  layout inset has one place to set. Feeds containerRowsFor (usePageNode). */
export const SECTION_CHROME_PX = 0;

/** The page-grid ROW count a section block needs to contain `contentBottomRows`
 *  rows of nested content — the SAME px-true fit `usePageNode`'s
 *  `containerRowsFor` computes at runtime, but as a pure helper so the SEED
 *  height matches the auto-grow height (a hard-coded template height was one
 *  row short of this, so the last widget overflowed the section — the Hero
 *  subtext bug). */
export function sectionBlockRows(contentBottomRows: number): number {
  const crh =
    typeof SECTION_NESTED_CONFIG.rowHeight === "number"
      ? SECTION_NESTED_CONFIG.rowHeight
      : PAGE_ROW_HEIGHT_PX;
  const cgap = SECTION_NESTED_CONFIG.gap ?? PAGE_GAP_PX;
  const cpad = SECTION_NESTED_CONFIG.padding ?? 0;
  const contentPx =
    cpad * 2 +
    contentBottomRows * crh +
    Math.max(0, contentBottomRows - 1) * cgap;
  return Math.max(
    1,
    Math.ceil(
      (contentPx + SECTION_CHROME_PX + PAGE_GAP_PX) /
        (PAGE_ROW_HEIGHT_PX + PAGE_GAP_PX),
    ),
  );
}

/** Bottom-most nested row a set of widgets occupies (max of y+h). */
export function contentRows(
  items: ReadonlyArray<{ position: { y: number; h: number } }>,
): number {
  let bottom = 0;
  for (const it of items) {
    const b = it.position.y + it.position.h;
    if (b > bottom) bottom = b;
  }
  return bottom;
}
